import {CM_LOGGER_TEMPLATES} from "@app/common/services/logging"
import {
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    Injector,
    input,
    linkedSignal,
    OnDestroy,
    OnInit,
    signal,
    ChangeDetectionStrategy,
    TemplateRef,
    untracked,
    viewChild,
    ViewContainerRef,
} from "@angular/core"
import {TemplatePortal} from "@angular/cdk/portal"
import {NgTemplateOutlet} from "@angular/common"
import {LocalStorageService} from "@common/services/local-storage/local-storage.service"
import {TemplateSidePanelService} from "@app/template-editor/services/template-side-panel.service"
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop"
import {MatDialog, MatDialogRef} from "@angular/material/dialog"
import {MatMenuModule} from "@angular/material/menu"
import {MatProgressBarModule} from "@angular/material/progress-bar"
import {MatTooltipModule} from "@angular/material/tooltip"
import {DialogComponent} from "@app/common/components/dialogs/dialog/dialog.component"
import {
    BatchOperationDialogComponent,
    BatchOperationDialogResult,
} from "@app/template-editor/components/batch-operation-dialog/batch-operation-dialog.component"
import {PermissionsService} from "@app/common/services/permissions/permissions.service"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {RenderingService} from "@app/common/services/rendering/rendering.service"
import {DIALOG_DEFAULT_WIDTH} from "@app/template-editor/helpers/constants"
import {
    backendPostProcessingInput,
    createLayerExportJobGraph,
    createRenderJob,
    discardJob,
    getAllJobAssignments,
    getAssignmentKey,
    getConfigurationString,
    getJobAssignments,
    JobData,
    parseAssignmentKey,
    postProcessingSettingsToImageProcessingSettings,
    UnassignedJobData,
    createBatchExportJobGraph,
    isJobError,
    isJobPending,
    BatchExportMediaType,
} from "@app/template-editor/helpers/template-jobs"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {
    buildTree,
    compareParts,
    ConfigInfo,
    familyContext,
    familyContextParts,
    layerContextKey,
    familyLayerCount,
    filterLayerPlanFamily,
    findLayerPlanContext,
    facetFilterEntry,
    findLayerPlanLayer,
    GroupRanking,
    LayerGroupRole,
    LayerPlan,
    LayerPlanContext,
    LayerPlanFamily,
    LayerPlanGroup,
    LayerPlanLayer,
    Parameters,
    parseLayerKey,
    ObjectPathPart,
    SceneNodes,
    TemplateNode,
    toggled,
    TreeCaptionRow,
    TreeNodeRow,
    VariationFilter,
    VariationGroup,
    variationFacets,
    VariationPart,
    withoutFacet,
} from "@cm/template-nodes"
import {sortedJSONStringify} from "@cm/utils/sorted-json-stringify"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {
    GetJobDetailsForTemplateImageViewerGQL,
    JobAssignmentDetailsForTemplateImageViewerFragment,
} from "@template-editor/components/template-image-viewer/template-image-viewer.component.generated"
import {TemplateJobManagerService} from "@template-editor/services/template-job-manager.service"
import {
    combineLatest,
    concatMap,
    EMPTY,
    finalize,
    firstValueFrom,
    from,
    map,
    of,
    switchMap,
    tap,
    catchError,
    defer,
    interval,
    startWith,
    Subject,
    takeUntil,
} from "rxjs"
import {ButtonComponent} from "@app/common/components/buttons/button/button.component"
import {ListItemComponent} from "@app/common/components/item/list-item/list-item.component"
import {TemplateJobIconComponent} from "@app/template-editor/components/template-job-icon/template-job-icon.component"
import {VariationSession, VariationWithSceneSettings} from "@app/template-editor/helpers/all-variations"
import {RunBackendJobOperation} from "@app/common/models/background-operation/run-backend-job-operation"
import {FilesService} from "@app/common/services/files/files.service"
import {BackgroundOperationService} from "@app/platform/services/background-operation/background-operation.service"
import {JobsAllVariationsGQL} from "@app/template-editor/components/template-all-variations/template-all-variations.generated"
import {JobState, JobType} from "@generated"
import {environment} from "@environment"
import {LoadingSpinnerIconComponent} from "@app/common/components/progress"
import {
    activeSolo,
    isInStack,
    exportableContexts,
    layerExrFolders,
    layerImageFileCount,
    layerImageFolders,
    layerLabel,
    layerSlots,
    LayerSelection,
    submitLayerRenderJobs,
} from "@app/template-editor/helpers/layer-jobs"

type ConfigurationData = VariationWithSceneSettings & {
    render?: JobData
}

type VariationRow = TreeNodeRow | TreeCaptionRow | {kind: "variation"; key: string; variation: ConfigurationData; label: string; groups: string; indent: number}

type ContextItem = {family: LayerPlanFamily; index: bigint; parts: VariationPart[]; name: string}

type ContextRow =
    | TreeNodeRow
    | TreeCaptionRow
    | {kind: "context"; key: string; context: LayerPlanContext; label: string; tooltip: string; renderedCount: number; current: boolean; indent: number}

const contextTreeLimit = 20_000

type RenderJobAssignment = {
    type: "render"
    configurationParameters: Parameters | undefined
    hash: string | undefined
    jobAssignment: JobAssignmentDetailsForTemplateImageViewerFragment & {assignmentKey: string}
}

// Every poll returns fresh job objects; comparing by value keeps unchanged rows from re-rendering.
function isSameRenderJob(a: JobData | undefined, b: JobData | undefined) {
    if (a === b) return true
    if (!a || !b) return false
    return (
        a.id === b.id &&
        a.name === b.name &&
        a.state === b.state &&
        a.progress === b.progress &&
        a.message === b.message &&
        JSON.stringify(a.output) === JSON.stringify(b.output)
    )
}

function isJobCompleted(job: JobData) {
    return !isJobPending(job) && !isJobError(job)
}

const contextRoles: LayerGroupRole[] = ["structure", "geometry", "scene"]
const tagOrder = ["geometry", "camera", "lights", "environment", "scene options", "render settings", "structure", "after assembly", "no effect"]

const classifyingStatus = "Classifying configurations…"

function familyHasLayer(family: LayerPlanFamily, context: LayerPlanContext, layer: LayerPlanLayer) {
    return (
        Object.entries(family.gateAssignment).every(([key, variantId]) => context.assignment[key] === variantId) &&
        family.contextGroups.every((group) => group.variants.some((variant) => variant.id === context.assignment[group.key])) &&
        (!layer.group ||
            family.layerGroups.some((group) => group.key === layer.group!.key && group.variants.some((variant) => variant.id === layer.variant!.id)))
    )
}

function listTooltip(prefix: string, items: string[]) {
    if (items.length === 0) return ""
    const maxShown = 8
    return `${prefix}: ${items.slice(0, maxShown).join(", ")}${items.length > maxShown ? `, … (${items.length - maxShown} more)` : ""}`
}

function layersPerContextLabel(families: readonly LayerPlanFamily[]) {
    const counts = families.map((family) => 1 + family.layerGroups.reduce((sum, group) => sum + group.variants.length, 0))
    if (counts.length === 0) return "0"
    const [min, max] = [Math.min(...counts), Math.max(...counts)]
    return min === max ? `${min}` : `${min}–${max}`
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-all-variations",
    imports: [
        MatProgressBarModule,
        MatTooltipModule,
        ListItemComponent,
        TemplateJobIconComponent,
        ButtonComponent,
        MatMenuModule,
        LoadingSpinnerIconComponent,
        NgTemplateOutlet,
    ],
    providers: [SceneManagerService],
    templateUrl: "./template-all-variations.component.html",
    styleUrl: "./template-all-variations.component.scss",
})
export class TemplateAllVariationsComponent implements OnInit, OnDestroy {
    private readonly logger = inject(CM_LOGGER_TEMPLATES)

    readonly $sceneManagerService = input.required<SceneManagerService>({alias: "sceneManagerService"})
    readonly $title = input<string | undefined>(undefined, {alias: "title"})

    private readonly localSceneManagerService = inject(SceneManagerService)
    protected readonly sidePanelService = inject(TemplateSidePanelService)
    private readonly viewContainerRef = inject(ViewContainerRef)
    private readonly $sidePanelTemplate = viewChild.required<TemplateRef<unknown>>("sidePanel")
    private readonly showListsInSidePanel = effect((onCleanup) => {
        this.sidePanelService.$portal.set(new TemplatePortal(this.$sidePanelTemplate(), this.viewContainerRef))
        onCleanup(() => this.sidePanelService.$portal.set(undefined))
    })
    private readonly destroyRef = inject(DestroyRef)
    private readonly injector = inject(Injector)
    private readonly matDialog = inject(MatDialog)
    private readonly notifications = inject(NotificationsService)
    private readonly renderingService = inject(RenderingService)
    private readonly templateJobManagerService = inject(TemplateJobManagerService)
    private readonly backgroundOperationService = inject(BackgroundOperationService)
    private readonly jobsAllVariationsGql = inject(JobsAllVariationsGQL)
    private readonly permission = inject(PermissionsService)
    readonly $can = this.permission.$to

    readonly jobGql = inject(GetJobDetailsForTemplateImageViewerGQL)

    private readonly $_progress = signal<number>(0)
    $progress = this.$_progress.asReadonly()

    // Persistent cancellation subject: we only emit, never complete, so it can be reused
    private readonly cancelSubject = new Subject<void>()

    cancelCurrentOperation() {
        if (this.$state() === "loading") {
            this.cancelSubject.next()
            this.$state.set("loaded")
            this.$_progress.set(0)
        }
    }

    readonly $state = signal<"loading" | "loaded" | "error">("loading")

    // Restarts after a facet click often finish within a few milliseconds; showing the bar for those makes the panel flicker.
    readonly $showProgress = signal(false)
    private readonly delayProgress = effect((onCleanup) => {
        if (this.$state() !== "loading") {
            this.$showProgress.set(false)
            return
        }
        const timer = setTimeout(() => this.$showProgress.set(true), 300)
        onCleanup(() => clearTimeout(timer))
    })

    readonly $fallbackPostProcessingSettings = computed(() => this.$sceneManagerService().$scene().find(SceneNodes.RenderPostProcessingSettings.is))

    // With a filter the list holds only matching variations, so absent jobs are not necessarily unassigned.
    private readonly $unassignedRenderJobs = computed(() => {
        if (this.$isFiltered() || this.$mode() === "layers") return []
        return this.$allRenderJobAssignments().filter(({jobAssignment}) => {
            return !this.$allVariations().some((variation) => variation.render?.id === jobAssignment.job.id)
        })
    })

    readonly $renderAllLayersDisabled = computed(() => this.$state() !== "loaded" || !this.$layerPlan())
    readonly $discardAllLayersDisabled = computed(() => this.$state() !== "loaded" || this.$listedLayerJobs().length === 0)
    readonly $downloadAllLayersDisabled = computed(() => this.$state() !== "loaded" || !this.$listedLayerJobs().some(({job}) => isJobCompleted(job)))

    readonly $renderAllDisabled = computed(() => {
        return this.$state() !== "loaded" || this.$allVariations().every((variation) => variation.render !== undefined)
    })
    readonly $downloadAllDisabled = computed(() => {
        if (this.$state() !== "loaded") return true
        const hasCompletedAssigned = this.$allVariations().some((variation) => variation.render !== undefined && isJobCompleted(variation.render))
        const hasCompletedUnassigned = this.$unassignedRenderJobs().some(({jobAssignment}) => isJobCompleted(jobAssignment.job))
        return !hasCompletedAssigned && !hasCompletedUnassigned
    })
    readonly $deleteAllDisabled = computed(() => {
        if (this.$state() !== "loaded") return true
        return !this.$allVariations().some((variation) => variation.render !== undefined) && this.$unassignedRenderJobs().length === 0
    })

    readonly variationRowPitch = 34
    private readonly variationOverscanRows = 10
    readonly $variationListScrollTop = signal(0)
    private readonly $variationListHeight = signal(0)
    private readonly variationList = viewChild<ElementRef<HTMLElement>>("variationList")
    private readonly observeVariationListHeight = effect((onCleanup) => {
        const element = this.variationList()?.nativeElement
        if (!element) return
        const observer = new ResizeObserver(() => this.$variationListHeight.set(element.clientHeight))
        observer.observe(element)
        onCleanup(() => observer.disconnect())
    })
    private readonly $listLength = computed(() =>
        this.$mode() === "layers" ? (this.$contextTreeRows()?.length ?? this.$layerContextCount()) : this.$variationRowCount(),
    )
    // Browsers cannot lay out elements much taller than this, so longer lists scroll proportionally.
    private readonly maxListSpacerHeight = 8_000_000
    readonly $listSpacerHeight = computed(() => Math.min(this.$listLength() * this.variationRowPitch, this.maxListSpacerHeight))
    readonly $visibleVariationRange = computed(() => {
        const count = this.$listLength()
        const height = this.$variationListHeight()
        const fullHeight = count * this.variationRowPitch
        const spacerHeight = this.$listSpacerHeight()
        // The browser clamps scrollTop when the list shrinks without always firing a scroll event, so the stored value can be stale.
        const scrollTop = Math.min(this.$variationListScrollTop(), Math.max(0, spacerHeight - height))
        const offset = spacerHeight < fullHeight ? (scrollTop / Math.max(1, spacerHeight - height)) * (fullHeight - height) : scrollTop
        const first = Math.max(0, Math.floor(offset / this.variationRowPitch) - this.variationOverscanRows)
        const last = Math.ceil((offset + height) / this.variationRowPitch) + this.variationOverscanRows
        return {
            start: first,
            end: Math.min(count, last),
            top: Math.floor(offset / this.variationRowPitch),
            translate: first * this.variationRowPitch - offset + scrollTop,
        }
    })
    private scrollVariationListToTop() {
        const element = this.variationList()?.nativeElement
        if (element) element.scrollTop = 0
        this.$variationListScrollTop.set(0)
    }
    readonly $allVariations = signal<ConfigurationData[]>([])
    readonly $totalVariations = signal<bigint | undefined>(undefined)
    readonly $variationCountLabel = computed(() => {
        const found = this.$allVariations().length.toLocaleString()
        const total = this.$totalVariations()
        return this.$state() === "loading" && total !== undefined ? `${found} / ${total.toLocaleString()}` : found
    })
    readonly $allConfigInfo = signal<ConfigInfo[]>([])

    readonly $analysisStatus = signal<string | undefined>(undefined)
    private readonly $session = signal<VariationSession | undefined>(undefined)
    private readonly $groups = signal<VariationGroup[]>([])
    private readonly $facets = computed(() => variationFacets(this.$groups()))
    private readonly $facetSelection = signal<Readonly<Record<string, string>>>({})
    readonly $filter = computed(
        (): VariationFilter =>
            this.$facets().flatMap((facet) => {
                const option = facet.options.find((candidate) => candidate.id === this.$facetSelection()[facet.id])
                return option ? [facetFilterEntry(facet, option)] : []
            }),
        {equal: (a, b) => sortedJSONStringify(a) === sortedJSONStringify(b)},
    )
    readonly $isFiltered = computed(() => this.$filter().length > 0)
    private readonly $optionCounts = signal<Map<string, Map<string, bigint>> | undefined>(undefined)
    private optionCountsRequest = 0

    private readonly $layerOptionCounts = computed(() => {
        const plan = this.$layerPlan()
        if (!plan) return undefined
        const filter = this.$filter()
        const result = new Map<string, Map<string, bigint>>()
        for (const facet of this.$facets()) {
            const others = withoutFacet(filter, facet)
            const families = plan.families.map((family) => filterLayerPlanFamily(family, others)).filter((family) => family !== undefined)
            const counts = new Map<string, bigint>()
            for (const option of facet.options) {
                const entry = [facetFilterEntry(facet, option)]
                counts.set(
                    option.id,
                    families.reduce((sum, family) => sum + (filterLayerPlanFamily(family, entry)?.contextCount ?? 0n), 0n),
                )
            }
            result.set(facet.id, counts)
        }
        return result
    })

    readonly $facetGroups = computed(() => {
        const layers = this.$mode() === "layers"
        const counts = layers ? this.$layerOptionCounts() : this.$optionCounts()
        const countTooltip = layers ? "Matching contexts" : "Matching variations"
        const selection = this.$facetSelection()
        return this.$facets()
            .map((facet) => {
                const facetCounts = counts?.get(facet.id)
                const options = facet.options.map((option) => {
                    const count = facetCounts?.get(option.id)
                    const isSelected = selection[facet.id] === option.id
                    return {
                        id: option.id,
                        name: option.name,
                        selected: isSelected,
                        countLabel: count === undefined ? "…" : count.toLocaleString(),
                        disabled: count === 0n && !isSelected,
                    }
                })
                const present = facet.id in selection || facetCounts === undefined || [...facetCounts.values()].some((count) => count > 0n)
                return {id: facet.id, name: facet.name, section: facet.section, options, present}
            })
            .filter((facet) => facet.present)
            .map((facet, index, facets) => {
                const {section} = facet
                const inSection = section !== undefined && facets.some((other) => other !== facet && other.section === section)
                const startsSection = inSection && facets[index - 1]?.section !== section
                return {...facet, countTooltip, inSection, sectionTitle: startsSection ? section : undefined}
            })
    })

    readonly $facetsExpanded = linkedSignal(() => this.sidePanelService.$open())
    readonly $selectedFacets = computed(() =>
        this.$facets().flatMap((facet) => {
            const option = facet.options.find((candidate) => candidate.id === this.$facetSelection()[facet.id])
            return option ? [{facet, option}] : []
        }),
    )

    readonly $variationSummary = computed(() => {
        const facets = this.$facets()
        if (facets.length === 0) return undefined
        const hidden = this.$hiddenLabelKeys()
        const counts = this.$optionCounts()
        const groups: string[] = []
        for (const facet of facets) {
            if (facet.keys.every((key) => hidden.has(key))) continue
            const facetCounts = counts?.get(facet.id)
            const possible = facetCounts ? [...facetCounts.values()].filter((count) => count > 0n).length : facet.options.length
            if (possible === 0) continue
            groups.push(`${facet.name} (${possible})${facet.keys.length > 1 ? ` ×${facet.keys.length}` : ""}`)
        }
        const total = this.$totalVariations() ?? BigInt(this.$allVariations().length)
        return {groups, total: total.toLocaleString()}
    })

    // Selected facets and facets with a single possible option are the same for every row, so labels leave them out.
    private readonly $hiddenLabelKeys = computed(() => {
        const hidden = new Set(this.$filter().flatMap((entry) => Object.keys(entry)))
        const counts = this.$optionCounts()
        const total = this.$totalVariations()
        if (counts && total !== undefined && total > 0n) {
            for (const facet of this.$facets()) {
                const possible = [...(counts.get(facet.id)?.values() ?? [])].filter((count) => count > 0n)
                if (possible.length === 1 && possible[0] === total) for (const key of facet.keys) hidden.add(key)
            }
        }
        return hidden
    })

    private partsLabel(parts: VariationPart[], fallback: string) {
        const hidden = this.$hiddenLabelKeys()
        const shown = parts.filter((part) => !hidden.has(part.key))
        return shown.length > 0 ? shown.map(({group, variant}) => `${group}: ${variant}`).join(", ") : fallback
    }

    readonly $variationView = signal<"tree" | "list">("tree")
    private readonly $expandedVariationNodes = signal<ReadonlySet<string>>(new Set())

    private readonly $groupRanking = computed((): GroupRanking => {
        const ranks = new Map<string, number>()
        const options = new Map<string, Map<string, number>>()
        for (const [rank, group] of this.$groups().entries()) {
            ranks.set(group.key, rank)
            options.set(group.key, new Map(group.variants.map((variant, index) => [variant.id, index])))
        }
        return {
            rankOf: (key) => {
                let rank = ranks.get(key)
                if (rank === undefined) {
                    rank = ranks.size
                    ranks.set(key, rank)
                }
                return rank
            },
            optionIndexOf: (key, variantId) => options.get(key)?.get(variantId) ?? Number.MAX_SAFE_INTEGER,
        }
    })

    private rankedPartsCache: {ranking: GroupRanking; parts: WeakMap<ConfigurationData, VariationPart[]>} | undefined

    private rankedParts(variation: ConfigurationData, ranking: GroupRanking) {
        if (this.rankedPartsCache?.ranking !== ranking) this.rankedPartsCache = {ranking, parts: new WeakMap()}
        let parts = this.rankedPartsCache.parts.get(variation)
        if (!parts) {
            parts = [...variation.parts].sort((a, b) => ranking.rankOf(a.key) - ranking.rankOf(b.key))
            this.rankedPartsCache.parts.set(variation, parts)
        }
        return parts
    }

    private shownPartsCache: {hidden: ReadonlySet<string>; ranking: GroupRanking; parts: WeakMap<ConfigurationData, VariationPart[]>} | undefined

    private shownParts(variation: ConfigurationData, hidden: ReadonlySet<string>) {
        const ranking = this.$groupRanking()
        if (this.shownPartsCache?.hidden !== hidden || this.shownPartsCache.ranking !== ranking) this.shownPartsCache = {hidden, ranking, parts: new WeakMap()}
        let parts = this.shownPartsCache.parts.get(variation)
        if (!parts) {
            parts = this.rankedParts(variation, ranking).filter((part) => !hidden.has(part.key))
            this.shownPartsCache.parts.set(variation, parts)
        }
        return parts
    }

    private variationRow(variation: ConfigurationData, parts: VariationPart[], indent: number): VariationRow {
        return {
            kind: "variation",
            key: variation.hash,
            variation,
            label: parts.map((part) => part.variant).join(" · ") || "Selected configuration",
            groups: parts.map((part) => part.group).join(" · "),
            indent,
        }
    }

    private readonly $variationTreeRows = computed(() => {
        const variations = this.$allVariations()
        const hidden = this.$hiddenLabelKeys()
        return buildTree(
            variations.length,
            (index) => this.shownParts(variations[index], hidden),
            (index, parts, indent) => this.variationRow(variations[index], parts, indent),
            this.$expandedVariationNodes(),
            this.$currentConfiguration(),
            (index) => {
                const render = variations[index].render
                return {done: render && isJobCompleted(render) ? 1 : 0, total: 1}
            },
            this.$groupRanking(),
        )
    })

    private readonly $variationRowCount = computed(() => (this.$variationView() === "tree" ? this.$variationTreeRows().length : this.$allVariations().length))

    readonly $visibleVariationRows = computed((): VariationRow[] => {
        const {start, end} = this.$visibleVariationRange()
        if (this.$variationView() === "tree") return this.$variationTreeRows().slice(start, end)
        const variations = this.$allVariations()
        const hidden = this.$hiddenLabelKeys()
        const rows: VariationRow[] = []
        for (let index = start; index < end; index++) rows.push(this.variationRow(variations[index], this.shownParts(variations[index], hidden), 0))
        return rows
    })

    readonly $variationLegend = computed(() => {
        if (this.$variationView() === "tree") return ""
        const {start, top} = this.$visibleVariationRange()
        const first = this.$visibleVariationRows()
            .slice(Math.max(0, top - start))
            .find((row) => row.kind === "variation")
        return first?.kind === "variation" ? first.groups : ""
    })

    toggleVariationNode(key: string) {
        this.$expandedVariationNodes.update((expanded) => toggled(expanded, key))
    }

    readonly $mode = signal<"fullFrames" | "layers">("fullFrames")
    readonly $layerPlan = signal<LayerPlan | undefined>(undefined)
    readonly $layerPlanUnavailable = signal(false)
    private readonly $fullVariationCount = signal<bigint | undefined>(undefined)
    private readonly $layerJobs = this.templateJobManagerService.$layerJobs
    private readonly $soloLayer = this.templateJobManagerService.$soloLayer
    private readonly $activeSolo = computed(() => {
        const plan = this.$layerPlan()
        return plan ? activeSolo(plan, this.$soloLayer(), this.$currentConfiguration()) : undefined
    })
    private originalNodes = new Map<unknown, TemplateNode>()
    private soloOutlineRequest = 0
    private readonly outlineSolo = effect(() => {
        const solo = this.$mode() === "layers" && this.$state() === "loaded" ? this.$activeSolo() : undefined
        untracked(() => void this.updateSoloOutline(solo))
    })

    // The panel compiles a clone of the template, so the parts of the soloed layer are mapped back to the original nodes the viewer shows.
    private async updateSoloOutline(solo: LayerSelection | undefined) {
        const request = ++this.soloOutlineRequest
        const sceneManagerService = this.$sceneManagerService()
        const group = solo?.layer.group
        if (!solo || !group) {
            sceneManagerService.$layerOutline.set([])
            return
        }
        try {
            const {base, slotsByGroup} = await layerSlots(this.localSceneManagerService, solo.context, [group.key])
            if (request !== this.soloOutlineRequest) return
            const meshes = new Map(base.filter(SceneNodes.Mesh.is).map((mesh) => [String(mesh.id), mesh]))
            const parts: ObjectPathPart[] = []
            for (const slotKey of slotsByGroup.get(group.key) ?? []) {
                const separator = slotKey.lastIndexOf("#")
                const mesh = meshes.get(slotKey.slice(0, separator))
                if (!mesh) continue
                parts.push({path: mesh.path.map((node) => this.originalNodes.get(node) ?? node), part: `group${Number(slotKey.slice(separator + 1))}`})
            }
            sceneManagerService.$layerOutline.set(parts)
        } catch (error) {
            this.logger.warn("Could not outline the soloed layer", error)
        }
    }

    private readonly shareLayerState = effect(() => {
        const layers = this.$mode() === "layers"
        this.templateJobManagerService.$layerMode.set(layers)
        this.templateJobManagerService.$layerPlan.set(layers ? this.$layerPlan() : undefined)
        if (!layers) this.$soloLayer.set(undefined)
    })
    private readonly $currentConfiguration = computed(() => this.$sceneManagerService().$currentLocalConfiguration().parameters)

    readonly $layerPlanSummary = computed(() => {
        const plan = this.$layerPlan()
        if (!plan) return undefined
        const optionCounts = new Map(this.$groups().map((group) => [group.key, group.variants.length]))
        // Sub-template instances repeat the same group names, so groups are listed once per name.
        const row = (
            label: string,
            tooltip: string,
            accept: (group: LayerPlanGroup) => boolean,
            describe: (group: LayerPlanGroup) => {tags: string[]; changes: string[]; changesPrefix: string},
        ) => {
            type Entry = {counts: Set<number>; instances: number; tags: Set<string>; changes: Set<string>; decides: Set<string>; changesPrefix: string}
            const byName = new Map<string, Entry>()
            for (const group of plan.groups) {
                if (!accept(group)) continue
                const {tags, changes, changesPrefix} = describe(group)
                const entry = byName.get(group.name) ?? {
                    counts: new Set(),
                    instances: 0,
                    tags: new Set(),
                    changes: new Set(),
                    decides: new Set(),
                    changesPrefix,
                }
                const count = optionCounts.get(group.key)
                if (count !== undefined) entry.counts.add(count)
                entry.instances++
                for (const tag of tags) entry.tags.add(tag)
                for (const change of changes) entry.changes.add(change)
                for (const decided of group.reasons.structure ?? []) entry.decides.add(decided)
                byName.set(group.name, entry)
            }
            const groups = [...byName].map(([name, {counts, instances, tags, changes, decides, changesPrefix}]) => {
                const options = counts.size > 0 ? ` (${[...counts].sort((a, b) => a - b).join("/")})` : ""
                return {
                    label: `${name}${options}${instances > 1 ? ` ×${instances}` : ""}`,
                    tag: tagOrder.filter((tag) => tags.has(tag)).join(", "),
                    tooltip: [listTooltip("Decides about", [...decides].sort()), listTooltip(changesPrefix, [...changes].sort())].filter(Boolean).join(" · "),
                }
            })
            return {label, tooltip, groups}
        }
        const full = this.$fullVariationCount()
        return {
            rows: [
                row(
                    "Contexts",
                    "Each option needs its own render of every layer",
                    (group) => group.roles.some((role) => contextRoles.includes(role)),
                    (group) => ({
                        tags: group.effects.length > 0 ? group.effects : ["structure"],
                        changes: [...(group.reasons.geometry ?? []), ...(group.reasons.scene ?? [])],
                        changesPrefix: "Changes",
                    }),
                ),
                row(
                    "Layers",
                    "Each option is one layer, rendered once per context",
                    (group) => group.roles.includes("material"),
                    (group) => ({tags: [], changes: group.reasons.material ?? [], changesPrefix: "Swaps materials on"}),
                ),
                row(
                    "No renders",
                    "Options need no renders of their own",
                    (group) => group.roles.every((role) => role === "post-processing" || role === "no effect"),
                    (group) => ({tags: [group.roles.includes("post-processing") ? "after assembly" : "no effect"], changes: [], changesPrefix: ""}),
                ),
            ].filter(({groups}) => groups.length > 0),
            contextCount: plan.contextCount.toLocaleString(),
            layerCount: plan.layerCount.toLocaleString(),
            layersPerContext: layersPerContextLabel(plan.families),
            fullCount: full?.toLocaleString(),
            warnings: plan.warnings,
        }
    })

    // Filtered families, each with the list index of its first context.
    private readonly $layerFamilies = computed(() => {
        const plan = this.$layerPlan()
        if (!plan) return []
        const filter = this.$filter()
        const families: {family: LayerPlanFamily; first: number}[] = []
        let first = 0
        for (const family of plan.families) {
            const filtered = filterLayerPlanFamily(family, filter)
            if (!filtered) continue
            families.push({family: filtered, first})
            first += Number(filtered.contextCount)
        }
        return families
    })
    private readonly $listedLayerJobs = computed(() => {
        const plan = this.$layerPlan()
        if (!plan) return []
        const families = this.$layerFamilies().map(({family}) => family)
        const listed: {key: string; job: JobData}[] = []
        for (const [key, job] of this.$layerJobs()) {
            const found = findLayerPlanLayer(plan, key)
            if (found && families.some((family) => familyHasLayer(family, found.context, found.layer))) listed.push({key, job})
        }
        return listed
    })

    private readonly $layerContextCount = computed(() => this.$layerFamilies().reduce((sum, {family}) => sum + Number(family.contextCount), 0))

    readonly $layerListTitle = computed(() => {
        const families = this.$layerFamilies().map(({family}) => family)
        const contexts = families.reduce((sum, family) => sum + family.contextCount, 0n)
        const layers = families.reduce((sum, family) => sum + familyLayerCount(family), 0n)
        return `Contexts (${contexts.toLocaleString()}) · ${layers.toLocaleString()} layer renders`
    })

    private readonly $layerJobsByReference = computed(() => {
        const plan = this.$layerPlan()
        const referenced: JobData[] = []
        const unreferenced: {key: string; job: JobData; label: string}[] = []
        if (!plan) return {referenced, unreferenced}
        for (const [key, job] of this.$layerJobs()) {
            if (findLayerPlanLayer(plan, key)) referenced.push(job)
            else unreferenced.push({key, job, label: this.layerKeyLabel(key)})
        }
        return {referenced, unreferenced}
    })
    readonly $unreferencedLayerJobs = computed(() => this.$layerJobsByReference().unreferenced)

    readonly $layerStats = computed(() => {
        if (!this.$layerPlan()) return undefined
        const jobs = this.$layerJobsByReference().referenced
        return {rendering: jobs.filter(isJobPending).length, rendered: jobs.filter(isJobCompleted).length, failed: jobs.filter(isJobError).length}
    })

    private readonly $currentLayerContext = computed(() => {
        const plan = this.$layerPlan()
        return plan ? findLayerPlanContext(plan, this.$currentConfiguration()) : undefined
    })

    readonly $contextView = signal<"tree" | "list">("tree")
    private readonly $expandedContextNodes = signal<ReadonlySet<string>>(new Set())
    readonly $contextTreeAvailable = computed(() => this.$layerContextCount() <= contextTreeLimit)

    private readonly $contextTreeItems = computed(() => {
        if (this.$contextView() !== "tree" || !this.$contextTreeAvailable()) return undefined
        const ranking = this.$groupRanking()
        const items: ContextItem[] = []
        for (const {family} of this.$layerFamilies()) {
            for (let index = 0n; index < family.contextCount; index++) {
                const parts = familyContextParts(family, index).sort((a, b) => ranking.rankOf(a.key) - ranking.rankOf(b.key))
                items.push({family, index, parts, name: parts.map((part) => `${part.group}: ${part.variant}`).join(", ")})
            }
        }
        return items.sort((a, b) => compareParts(a.parts, b.parts, ranking) || this.variationNameCollator.compare(a.name, b.name))
    })

    private readonly $contextTreeRows = computed(() => {
        const items = this.$contextTreeItems()
        if (!items) return undefined
        const hidden = this.$hiddenLabelKeys()
        const shown = items.map((item) => item.parts.filter((part) => !hidden.has(part.key)))
        const rendered = this.$renderedLayersByContext()
        return buildTree(
            items.length,
            (index) => shown[index],
            (index, parts, indent) => ({kind: "contextItem" as const, item: items[index], parts, indent}),
            this.$expandedContextNodes(),
            this.$currentConfiguration(),
            (index) => {
                const {family, parts} = items[index]
                const done = rendered.size === 0 ? 0 : (rendered.get(layerContextKey(Object.fromEntries(parts.map((part) => [part.key, part.variantId])))) ?? 0)
                return {done, total: 1 + family.layerGroups.reduce((sum, group) => sum + group.variants.length, 0)}
            },
            this.$groupRanking(),
        )
    })

    private readonly $renderedLayersByContext = computed(() => {
        const plan = this.$layerPlan()
        const counts = new Map<string, number>()
        if (!plan) return counts
        for (const [key, job] of this.$layerJobs()) {
            if (!isJobCompleted(job)) continue
            const found = findLayerPlanLayer(plan, key)
            if (found) counts.set(found.context.key, (counts.get(found.context.key) ?? 0) + 1)
        }
        return counts
    })

    private contextRow(context: LayerPlanContext, shownParts: VariationPart[], indent: number): ContextRow {
        const jobs = this.$layerJobs()
        return {
            kind: "context",
            key: context.key,
            context,
            label: shownParts.map((part) => part.variant).join(" · ") || "All configurations",
            tooltip: this.partsLabel(context.parts, "All configurations"),
            renderedCount: context.layers.filter((layer) => jobs.has(layer.key) && isJobCompleted(jobs.get(layer.key)!)).length,
            current: context.key === this.$currentLayerContext()?.key,
            indent,
        }
    }

    readonly $visibleLayerContexts = computed((): ContextRow[] => {
        const {start, end} = this.$visibleVariationRange()
        const treeRows = this.$contextTreeRows()
        if (treeRows) {
            return treeRows
                .slice(start, end)
                .map((row) => (row.kind === "contextItem" ? this.contextRow(familyContext(row.item.family, row.item.index), row.parts, row.indent) : row))
        }
        const families = this.$layerFamilies()
        const hidden = this.$hiddenLabelKeys()
        const rows: ContextRow[] = []
        let familyIndex = families.findLastIndex(({first}) => first <= start)
        for (let index = start; index < end && familyIndex >= 0; index++) {
            while (familyIndex + 1 < families.length && families[familyIndex + 1].first <= index) familyIndex++
            const {family, first} = families[familyIndex]
            const context = familyContext(family, BigInt(index - first))
            rows.push(
                this.contextRow(
                    context,
                    context.parts.filter((part) => !hidden.has(part.key)),
                    0,
                ),
            )
        }
        return rows
    })

    readonly $layerContextLegend = computed(() => {
        if (this.$contextTreeRows()) return ""
        const {start, top} = this.$visibleVariationRange()
        const first = this.$visibleLayerContexts()[Math.max(0, top - start)]
        if (first?.kind !== "context") return ""
        const hidden = this.$hiddenLabelKeys()
        return first.context.parts
            .filter((part) => !hidden.has(part.key))
            .map((part) => part.group)
            .join(" · ")
    })

    toggleContextNode(key: string) {
        this.$expandedContextNodes.update((expanded) => toggled(expanded, key))
    }

    readonly $currentContextLayers = computed(() => {
        const context = this.$currentLayerContext()
        if (!context) return undefined
        const configuration = this.$currentConfiguration()
        const jobs = this.$layerJobs()
        const solo = this.$activeSolo()
        return context.layers.map((layer, index) => ({
            context,
            layer,
            label: layerLabel(layer),
            job: jobs.get(layer.key),
            inStack: isInStack(layer, configuration),
            solo: solo?.layer.key === layer.key,
            startsGroup: index > 0 && layer.group?.key !== context.layers[index - 1].group?.key,
        }))
    })

    private layerKeyLabel(key: string) {
        const parsed = parseLayerKey(key)
        if (!parsed) return key
        const groups = new Map(this.$groups().map((group) => [group.key, group]))
        const describe = (groupKey: string, variantId: string) => {
            const group = groups.get(groupKey)
            return `${group?.name ?? groupKey}: ${group?.variants.find((variant) => variant.id === variantId)?.name ?? variantId}`
        }
        const context = Object.entries(parsed.context)
            .map(([groupKey, variantId]) => describe(groupKey, variantId))
            .join(", ")
        const layer = parsed.group === undefined ? "Rest" : describe(parsed.group, parsed.variant!)
        return context ? `${context} · ${layer}` : layer
    }

    clickLayer(context: LayerPlanContext, layer: LayerPlanLayer) {
        this.selectLayer(context, layer)
        if (this.$activeSolo()) this.$soloLayer.set({context, layer})
    }

    toggleSolo(context: LayerPlanContext, layer: LayerPlanLayer) {
        if (this.$activeSolo()?.layer.key === layer.key) {
            this.$soloLayer.set(undefined)
            return
        }
        this.selectLayer(context, layer)
        this.$soloLayer.set({context, layer})
    }

    // Material choices the context offers are kept from the current configuration, so stepping through layers changes one thing at a time.
    selectLayer(context: LayerPlanContext, layer?: LayerPlanLayer) {
        const configuration = this.$currentConfiguration()
        const parameters: Record<string, string> = {...context.assignment}
        for (const candidate of context.layers) {
            if (candidate.group && isInStack(candidate, configuration)) parameters[candidate.group.key] = candidate.variant!.id
        }
        if (layer?.group) parameters[layer.group.key] = layer.variant!.id
        this.$sceneManagerService().$instanceParameters.set(new Parameters({parameters}))
        this.$selectedUnassignedJob.set(undefined)
    }

    private layerPlanUpdates(session: VariationSession) {
        return defer(() => {
            if (!this.$layerPlan()) this.$analysisStatus.set(classifyingStatus)
            return from(session.count([])).pipe(
                tap((count) => this.$fullVariationCount.set(count)),
                switchMap(() => session.layerPlan((fraction) => this.$_progress.set(fraction * 100))),
                tap((plan) => {
                    this.$layerPlan.set(plan)
                    this.$layerPlanUnavailable.set(plan === undefined)
                }),
                map(() => "done" as const),
                finalize(() => this.$analysisStatus.update((status) => (status === classifyingStatus ? undefined : status))),
            )
        })
    }

    toggleFacet(facet: string, option: string) {
        this.$facetSelection.update((selection) => {
            const next = {...selection}
            if (next[facet] === option) delete next[facet]
            else next[facet] = option
            return next
        })
    }

    clearFilter() {
        this.$facetSelection.set({})
    }

    private updateOptionCounts(session: VariationSession, filter: VariationFilter) {
        const request = ++this.optionCountsRequest
        const isStale = () => request !== this.optionCountsRequest
        this.$optionCounts.set(undefined)
        session.optionCounts(filter, isStale).then(
            (counts) => {
                if (isStale()) return
                this.$optionCounts.set(counts)
                if (!session.supportsCounting) this.$groups.set([])
            },
            (error) => {
                if (!isStale()) this.logger.error("Error while counting variations", error)
            },
        )
    }
    private readonly $allRenderJobAssignments = signal<RenderJobAssignment[]>([])
    private readonly $renderJobByHash = computed(() => {
        const jobByHash = new Map<string, JobData>()
        for (const {hash, jobAssignment} of this.$allRenderJobAssignments()) {
            if (hash !== undefined && !jobByHash.has(hash)) jobByHash.set(hash, jobAssignment.job)
        }
        return jobByHash
    })
    private readonly variationNameCollator = new Intl.Collator()
    private pendingVariations: ConfigurationData[] = []
    private pendingVariationsFlush: ReturnType<typeof setTimeout> | undefined

    private flushPendingVariations() {
        clearTimeout(this.pendingVariationsFlush)
        this.pendingVariationsFlush = undefined
        if (this.pendingVariations.length === 0) return

        const ranking = this.$groupRanking()
        const byName = (a: ConfigurationData, b: ConfigurationData) =>
            compareParts(this.rankedParts(a, ranking), this.rankedParts(b, ranking), ranking) || this.variationNameCollator.compare(a.name, b.name)
        const batch = this.pendingVariations.sort(byName)
        this.pendingVariations = []
        this.$allVariations.update((sorted) => {
            const merged: ConfigurationData[] = []
            let i = 0
            let j = 0
            while (i < sorted.length && j < batch.length) merged.push(byName(batch[j], sorted[i]) < 0 ? batch[j++] : sorted[i++])
            while (i < sorted.length) merged.push(sorted[i++])
            while (j < batch.length) merged.push(batch[j++])
            return merged
        })
    }

    private discardPendingVariations() {
        clearTimeout(this.pendingVariationsFlush)
        this.pendingVariationsFlush = undefined
        this.pendingVariations = []
    }
    readonly $numRendering = computed(() => {
        return this.$allRenderJobAssignments().filter(({jobAssignment}) => isJobPending(jobAssignment.job)).length
    })
    readonly $numRendered = computed(() => {
        return this.$allRenderJobAssignments().filter(({jobAssignment}) => isJobCompleted(jobAssignment.job)).length
    })
    readonly $numFailed = computed(() => {
        return this.$allRenderJobAssignments().filter(({jobAssignment}) => isJobError(jobAssignment.job)).length
    })

    $unassignedJobs = this.templateJobManagerService.$unassignedJobs
    readonly developmentMode = inject(LocalStorageService).developmentMode
    readonly $renderQueueGroups = this.templateJobManagerService.$renderQueueGroups
    readonly $renderQueueGroup = this.templateJobManagerService.$renderQueueGroup
    private readonly $queueGroupSelect = computed(() =>
        this.developmentMode && this.$renderQueueGroups().length > 0
            ? {
                  label: "Render queue group",
                  options: [{value: "", label: "Cloud rendering"}, ...this.$renderQueueGroups().map((queueGroup) => ({value: queueGroup, label: queueGroup}))],
                  $value: this.$renderQueueGroup,
              }
            : undefined,
    )
    $selectedUnassignedJob = this.templateJobManagerService.$selectedUnassignedJob

    readonly $currentLocalConfigurationHash = computed(() => {
        return this.$sceneManagerService().$currentLocalConfiguration().getHash()
    })

    async setConfiguration(configuration: ConfigurationData) {
        this.$sceneManagerService().$instanceParameters.set(configuration.parameters)

        const templateRevisionId = this.$sceneManagerService().$templateRevisionId()
        if (!templateRevisionId) return

        const configurationString = getConfigurationString(configuration.parameters)
        const [render] = await getJobAssignments(this.injector, templateRevisionId, [getAssignmentKey("render", configurationString)])

        this.$allVariations.update((variations) => variations.map((v) => (v.hash === configuration.hash ? {...configuration, render} : v)))
        if (!render) this.$allRenderJobAssignments.update((assignments) => assignments.filter((a) => a.hash !== configuration.hash))
        else this.$allRenderJobAssignments.update((assignments) => assignments.map((a) => (a.hash === configuration.hash ? {...a, job: render} : a)))

        this.$selectedUnassignedJob.set(undefined)
    }

    private displayName(configuration: Parameters | undefined) {
        const allConfigInfo = this.$allConfigInfo()

        const fallbackDisplayName = "Unknown config(s)"
        if (!configuration) return fallbackDisplayName

        const configNames: string[] = []
        Object.entries(configuration.parameters).forEach(([key, value]) => {
            const configName = allConfigInfo.find((x) => x.props.id === key)?.props.name ?? key
            const variantName = allConfigInfo.find((x) => x.props.id === key)?.props.variants.find((x) => x.id === value)?.name ?? value

            configNames.push(`${configName}: ${variantName}`)
        })

        return configNames.length > 0 ? configNames.join(", ") : fallbackDisplayName
    }

    private refreshUnassignedJobs() {
        if (this.$isFiltered() || this.$mode() === "layers") return
        this.$unassignedJobs.set(
            this.$unassignedRenderJobs().map(({type, configurationParameters, jobAssignment}) => {
                return {
                    job: jobAssignment.job,
                    displayName: this.displayName(configurationParameters),
                    type,
                }
            }),
        )
    }

    ngOnInit() {
        if (this.developmentMode) void this.templateJobManagerService.loadRenderQueueGroups()
        this.localSceneManagerService.$lodType.set("pathTraced")
        this.localSceneManagerService.$computeDummyReifiedMeshData.set(true)

        this.localSceneManagerService.$exposeClaimedSubTemplateInputs.set(true)
        this.localSceneManagerService.$autoCompile.set(false)

        const sceneManagerService = this.$sceneManagerService()

        combineLatest([sceneManagerService.templateRevisionId$, sceneManagerService.defaultCustomerId$, sceneManagerService.templateGraph$])
            .pipe(
                tap(() => {
                    this.$analysisStatus.set(undefined)
                    this.$session.set(undefined)
                    this.$groups.set([])
                    this.$facetSelection.set({})
                    this.$layerPlan.set(undefined)
                    this.$layerPlanUnavailable.set(false)
                    this.$fullVariationCount.set(undefined)
                    this.$state.set("loading")
                }),
                switchMap(([templateRevisionId, defaultCustomerId, templateGraph]) => {
                    this.localSceneManagerService.$templateRevisionId.set(templateRevisionId)
                    this.localSceneManagerService.$defaultCustomerId.set(defaultCustomerId)
                    const originalNodes = new Map<unknown, TemplateNode>()
                    const clonedTemplateGraph = templateGraph.clone({
                        cloneSubNode: () => true,
                        onCloned: (original, copy) => originalNodes.set(copy, original as TemplateNode),
                    })
                    this.originalNodes = originalNodes
                    this.localSceneManagerService.$templateGraph.set(clonedTemplateGraph)
                    return from(
                        VariationSession.create(
                            this.localSceneManagerService,
                            (status) => this.$analysisStatus.set(status),
                            (fraction) => this.$_progress.set(fraction * 100),
                        ),
                    )
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (session) => {
                    this.$analysisStatus.set(undefined)
                    this.$groups.set(session.groups())
                    this.$session.set(session)
                },
                error: (e) => {
                    this.$state.set("error")
                    this.logger.error("Error while analyzing variations", e)
                },
            })

        const cancel$ = this.cancelSubject
        const configInfoSideEffect = (configInfo: ConfigInfo) => {
            this.$allConfigInfo.update((oldValue) => {
                if (oldValue.some((x) => x.props.id === configInfo.props.id)) return oldValue
                return [...oldValue, configInfo]
            })
        }
        combineLatest([
            toObservable(this.$session, {injector: this.injector}),
            toObservable(this.$filter, {injector: this.injector}),
            toObservable(this.$mode, {injector: this.injector}),
        ])
            .pipe(
                switchMap(([session, filter, mode]) => {
                    this.discardPendingVariations()
                    this.$allVariations.set([])
                    this.scrollVariationListToTop()
                    this.$totalVariations.set(undefined)
                    this.$_progress.set(0)
                    if (!session) return EMPTY

                    this.$state.set("loading")
                    this.updateOptionCounts(session, filter)
                    if (mode === "layers") {
                        return this.layerPlanUpdates(session).pipe(
                            takeUntil(cancel$),
                            catchError((e) => {
                                this.$state.set("error")
                                this.logger.error("Error while classifying configurations", e)
                                return EMPTY
                            }),
                        )
                    }
                    return session
                        .enumerate(
                            filter,
                            (progress) => this.$_progress.set(progress),
                            configInfoSideEffect,
                            (total) => this.$totalVariations.set(total),
                        )
                        .pipe(
                            map((variation) => {
                                if (variation === "done") return "done"
                                return {...variation, render: this.$renderJobByHash().get(variation.hash)}
                            }),
                            takeUntil(cancel$),
                            catchError((e) => {
                                this.$state.set("error")
                                this.logger.error("Error while gathering variations", e)
                                return EMPTY
                            }),
                        )
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((variation) => {
                if (variation === "done") {
                    this.flushPendingVariations()
                    this.logger.debug("Done gathering variations", this.$allVariations().length)
                    this.refreshUnassignedJobs()
                    this.$state.set("loaded")
                } else {
                    this.pendingVariations.push(variation)
                    this.pendingVariationsFlush ??= setTimeout(() => this.flushPendingVariations(), 100)
                }
            })

        this.localSceneManagerService.templateRevisionId$
            .pipe(
                switchMap((templateRevisionId) => {
                    if (!templateRevisionId) return of(null)
                    return interval(10000).pipe(
                        startWith(0), // run immediately, then every 10s
                        switchMap(() =>
                            from(getAllJobAssignments(this.injector, templateRevisionId)).pipe(
                                map((jobAssignments) => {
                                    const assignments = jobAssignments.filter((jobAssignment) => jobAssignment !== null)
                                    const layerJobs = new Map<string, JobData>()
                                    for (const {assignmentKey, job} of assignments) {
                                        if (assignmentKey && parseLayerKey(assignmentKey)) layerJobs.set(assignmentKey, job)
                                    }
                                    const renderJobAssignments = assignments
                                        .map((jobAssignment) => {
                                            const parsed = jobAssignment.assignmentKey ? parseAssignmentKey(jobAssignment.assignmentKey) : undefined
                                            return {
                                                type: parsed?.type,
                                                configurationParameters: parsed?.configurationParameters,
                                                hash: parsed?.configurationParameters?.getHash(),
                                                jobAssignment,
                                            }
                                        })
                                        .filter((jobInfo): jobInfo is RenderJobAssignment => jobInfo.type === "render" && !!jobInfo.jobAssignment.assignmentKey)
                                    return {renderJobAssignments, layerJobs}
                                }),
                            ),
                        ),
                    )
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((jobs) => {
                if (!jobs) {
                    this.$allRenderJobAssignments.set([])
                    this.$layerJobs.set(new Map())
                    return
                }
                this.$allRenderJobAssignments.set(jobs.renderJobAssignments)
                this.$layerJobs.set(jobs.layerJobs)

                const renderJobByHash = this.$renderJobByHash()
                this.$allVariations.update((variations) =>
                    variations.map((variation) => {
                        const render = renderJobByHash.get(variation.hash)
                        return isSameRenderJob(render, variation.render) ? variation : {...variation, render}
                    }),
                )

                if (this.$state() !== "loading") {
                    this.refreshUnassignedJobs()
                }
            })

        this.$unassignedJobs.set([])
        this.$selectedUnassignedJob.set(undefined)
    }

    async renderAll() {
        const templateRevisionId = this.localSceneManagerService.$templateRevisionId()
        if (!templateRevisionId) throw Error("No template revision id set")

        const defaultCustomerId = this.localSceneManagerService.$defaultCustomerId()
        if (!defaultCustomerId) throw Error("No default customer id set")

        const runningJobs = await fetchThrowingErrors(this.jobsAllVariationsGql)({
            filter: {
                organizationLegacyId: {equals: defaultCustomerId},
                state: [JobState.Init, JobState.Runnable, JobState.Running],
            },
        })

        const variations = this.$allVariations().filter((variation) => variation.render === undefined)
        const allowedJobCount = Math.max(environment.rendering.maxConcurrentRenderJobs - runningJobs.jobs.length, 0)

        const dialogRef: MatDialogRef<DialogComponent, boolean> = this.matDialog.open(DialogComponent, {
            disableClose: false,
            width: DIALOG_DEFAULT_WIDTH,
            data: {
                title: "Submit renders",
                message:
                    variations.length <= allowedJobCount
                        ? `You are submitting <b>${variations.length}</b> render jobs.</br></br>Are you sure you want to continue?`
                        : `You are trying to submit <b>${variations.length}</b> render jobs, but only <b>${allowedJobCount}</b> more jobs can be submitted at this time due to system limits.</br></br>The first <b>${allowedJobCount}</b> jobs will be submitted. Are you sure you want to continue?`,
                confirmLabel: "Submit jobs",
                cancelLabel: "Cancel",
                select: this.$queueGroupSelect(),
            },
        })

        variations.splice(allowedJobCount)

        const confirmed = await firstValueFrom(dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)))
        if (confirmed !== true) return

        let numSubmitted = 0
        let numFailed = 0
        this.$_progress.set(0)
        this.$state.set("loading")

        const queueGroup = this.$renderQueueGroup() || undefined
        const cancel$ = this.cancelSubject
        from(variations)
            .pipe(
                concatMap((variation) => {
                    const variationString = getConfigurationString(variation.parameters)
                    return defer(() => {
                        this.localSceneManagerService.$instanceParameters.set(variation.parameters)
                        this.localSceneManagerService.compileTemplate()
                        return from(this.localSceneManagerService.syncSolved(true)).pipe(
                            switchMap(() =>
                                from(
                                    createRenderJob(
                                        this.injector,
                                        this.renderingService,
                                        templateRevisionId,
                                        variationString,
                                        this.localSceneManagerService.$scene(),
                                        defaultCustomerId,
                                        queueGroup,
                                    ),
                                ),
                            ),
                            tap(([_, job]) => {
                                this.$allVariations.update((variations) => variations.map((v) => (v.hash === variation.hash ? {...variation, render: job} : v)))
                                numSubmitted += 1
                                this.$_progress.set(((numSubmitted + numFailed) / variations.length) * 100)
                            }),
                        )
                    }).pipe(
                        catchError((e) => {
                            this.logger.error("Error while submitting job", e)
                            numFailed += 1
                            this.$_progress.set(((numSubmitted + numFailed) / variations.length) * 100)
                            return of(null)
                        }),
                    )
                }),
                takeUntil(cancel$),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                complete: () => {
                    this.$state.set(numFailed === 0 ? "loaded" : "error")
                    this.notifications.showInfo(`Submitted ${numSubmitted} render jobs.${numFailed > 0 ? ` Failed to submit ${numFailed} jobs.` : ""}`)
                },
            })
    }

    async renderAllLayers() {
        const templateRevisionId = this.localSceneManagerService.$templateRevisionId()
        if (!templateRevisionId) throw Error("No template revision id set")
        const defaultCustomerId = this.localSceneManagerService.$defaultCustomerId()
        if (!defaultCustomerId) throw Error("No default customer id set")

        const runningJobs = await fetchThrowingErrors(this.jobsAllVariationsGql)({
            filter: {
                organizationLegacyId: {equals: defaultCustomerId},
                state: [JobState.Init, JobState.Runnable, JobState.Running],
            },
        })
        const allowedJobCount = Math.max(environment.rendering.maxConcurrentRenderJobs - runningJobs.jobs.length, 0)

        const jobs = this.$layerJobs()
        const work: {context: LayerPlanContext; layers: LayerPlanLayer[]}[] = []
        let count = 0
        let limited = false
        for (const {family} of this.$layerFamilies()) {
            for (let index = 0n; index < family.contextCount && !limited; index++) {
                const context = familyContext(family, index)
                const pending = context.layers.filter((layer) => !jobs.has(layer.key))
                if (pending.length === 0) continue
                const taken = pending.slice(0, allowedJobCount - count)
                if (taken.length > 0) work.push({context, layers: taken})
                count += taken.length
                limited = taken.length < pending.length
            }
            if (limited) break
        }
        if (work.length === 0 && !limited) {
            this.notifications.showInfo("All listed layers are rendered.")
            return
        }

        const dialogRef: MatDialogRef<DialogComponent, boolean> = this.matDialog.open(DialogComponent, {
            disableClose: false,
            width: DIALOG_DEFAULT_WIDTH,
            data: {
                title: "Submit layer renders",
                message: limited
                    ? `More layers are missing, but only <b>${count}</b> more jobs can be submitted at this time due to system limits.</br></br>The first <b>${count}</b> jobs will be submitted. Are you sure you want to continue?`
                    : `You are submitting <b>${count}</b> layer render jobs.</br></br>Are you sure you want to continue?`,
                confirmLabel: "Submit jobs",
                cancelLabel: "Cancel",
                select: this.$queueGroupSelect(),
            },
        })
        const confirmed = await firstValueFrom(dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)))
        if (confirmed !== true || count === 0) return

        let submitted = 0
        let failed = 0
        let cancelled = false
        const cancellation = this.cancelSubject.subscribe(() => (cancelled = true))
        this.$_progress.set(0)
        this.$state.set("loading")
        try {
            for (const {context, layers} of work) {
                if (cancelled) break
                const result = await submitLayerRenderJobs({
                    injector: this.injector,
                    renderingService: this.renderingService,
                    sceneManagerService: this.localSceneManagerService,
                    templateRevisionId,
                    organizationLegacyId: defaultCustomerId,
                    queueGroup: this.$renderQueueGroup() || undefined,
                    context,
                    layers,
                    onSubmitted: (layer, job) => this.$layerJobs.update((current) => new Map(current).set(layer.key, job)),
                    onError: (_layer, error) => this.logger.error("Error while submitting a layer render", error),
                    isCancelled: () => cancelled,
                })
                submitted += result.submitted
                failed += result.failed
                this.$_progress.set(((submitted + failed) / count) * 100)
            }
        } catch (error) {
            this.logger.error("Error while submitting layer renders", error)
        } finally {
            cancellation.unsubscribe()
        }
        this.$state.set(failed === 0 ? "loaded" : "error")
        this.notifications.showInfo(`Submitted ${submitted} layer render jobs.${failed > 0 ? ` Failed to submit ${failed} jobs.` : ""}`)
    }

    async downloadAllLayers(format: "exr" | "png" | "tiff") {
        const plan = this.$layerPlan()
        if (!plan) return
        const defaultCustomerId = this.localSceneManagerService.$defaultCustomerId()
        if (!defaultCustomerId) throw Error("No default customer id set")
        const templateRevisionId = this.localSceneManagerService.$templateRevisionId()

        const session = this.$session()
        if (!session) return
        const {exportable, skipped} = exportableContexts(plan, this.$listedLayerJobs())
        const exrFolders = format === "exr" ? layerExrFolders(exportable) : undefined
        const fileCount = exrFolders ? exrFolders.reduce((sum, folder) => sum + folder.files.length, 0) : layerImageFileCount(exportable)
        const dialogRef: MatDialogRef<DialogComponent, boolean> = this.matDialog.open(DialogComponent, {
            disableClose: false,
            width: DIALOG_DEFAULT_WIDTH,
            data: {
                title: "Download layers",
                message:
                    `You are downloading <b>${fileCount}</b> ${format === "exr" ? "linear EXR" : format.toUpperCase()} files in <b>${exportable.length}</b> context folders.` +
                    (skipped > 0
                        ? `</br></br><b>${skipped}</b> contexts are left out, because their rest layer or every option of a group is not finished yet.`
                        : ""),
                confirmLabel: "Submit job",
                cancelLabel: "Cancel",
            },
        })
        const confirmed = await firstValueFrom(dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)))
        if (confirmed !== true) return

        const exportName = `${this.$title() || `Template (${templateRevisionId})`} layers`
        const folders =
            exrFolders ??
            layerImageFolders(
                exportable,
                async (context) => {
                    const settings = await session.postProcessingSettings(context.assignment)
                    return settings ? postProcessingSettingsToImageProcessingSettings(settings) : {}
                },
                format === "png" ? "image/png" : "image/tiff",
                format,
                (job) => backendPostProcessingInput(this.injector, job),
            )
        const runBackendJobOperation = new RunBackendJobOperation(
            this.injector,
            "Download",
            `Preparing: ${exportName}`,
            {
                jobType: JobType.BatchDownload,
                name: `Layer download for template ${templateRevisionId}`,
                graph: Promise.resolve(folders).then((folders) => createLayerExportJobGraph(folders, defaultCustomerId, exportName)),
                organizationLegacyId: defaultCustomerId,
            },
            (jobData, setResult) => {
                const resultUrl = jobData.output?.downloadUrl
                if (!resultUrl) return
                setResult({url: resultUrl, title: "Download link"})
                FilesService.downloadFile(exportName, resultUrl)
            },
        )
        this.backgroundOperationService.registerBackgroundOperation(runBackendJobOperation)
        void runBackendJobOperation.submitJob()
    }

    async discardAllLayers() {
        const listed = this.$listedLayerJobs()
        const dialogRef: MatDialogRef<DialogComponent, boolean> = this.matDialog.open(DialogComponent, {
            disableClose: false,
            width: DIALOG_DEFAULT_WIDTH,
            data: {
                title: "Discard layer renders",
                message: `You are discarding <b>${listed.length}</b> layer renders.</br></br>Are you sure you want to continue?`,
                confirmLabel: "Discard",
                cancelLabel: "Cancel",
            },
        })
        const confirmed = await firstValueFrom(dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)))
        if (confirmed !== true) return

        let discarded = 0
        let failed = 0
        this.$_progress.set(0)
        this.$state.set("loading")
        for (const {key, job} of listed) {
            try {
                await discardJob(this.injector, job.id)
                this.$layerJobs.update((current) => {
                    if (current.get(key)?.id !== job.id) return current
                    const next = new Map(current)
                    next.delete(key)
                    return next
                })
                discarded++
            } catch (error) {
                this.logger.error("Error while discarding a layer render", error)
                failed++
            }
            this.$_progress.set(((discarded + failed) / listed.length) * 100)
        }
        this.$state.set(failed === 0 ? "loaded" : "error")
        this.templateJobManagerService.jobsDiscarded$.next()
        this.notifications.showInfo(`Discarded ${discarded} layer renders.${failed > 0 ? ` Failed to discard ${failed}.` : ""}`)
    }

    async downloadAll(mediaType: BatchExportMediaType) {
        const variations = this.$allVariations().filter(
            (variation): variation is ConfigurationData & {render: JobData} => variation.render !== undefined && isJobCompleted(variation.render),
        )

        const templateRevisionId = this.localSceneManagerService.$templateRevisionId()
        if (!templateRevisionId) throw Error("No template revision id set")

        const defaultCustomerId = this.localSceneManagerService.$defaultCustomerId()
        if (!defaultCustomerId) throw Error("No default customer id set")

        const variationsData = variations.map((variation) => ({
            configurationString: getConfigurationString(variation.parameters),
            name: variation.name,
            postProcessingSettings: variation.postProcessingSettings,
        }))

        const unmatchedRenderJobs = this.$unassignedRenderJobs()
            .filter(({jobAssignment}) => isJobCompleted(jobAssignment.job))
            .map(({jobAssignment, configurationParameters}) => ({
                configurationString: jobAssignment.assignmentKey.replace("render:", ""),
                name: this.displayName(configurationParameters),
                postProcessingSettings: this.$fallbackPostProcessingSettings(),
            }))

        const dialogRef: MatDialogRef<BatchOperationDialogComponent, BatchOperationDialogResult | undefined> = this.matDialog.open(
            BatchOperationDialogComponent,
            {
                disableClose: false,
                width: DIALOG_DEFAULT_WIDTH,
                data: {
                    configurationCount: variationsData.length,
                    unassignedCount: unmatchedRenderJobs.length,
                    title: "Batch download renders",
                    description: "You are submitting batch download of rendered images for",
                    confirmLabel: "Submit job",
                },
            },
        )

        const result = await firstValueFrom(dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)))
        if (!result) return

        if (result.includeUnassigned) {
            variationsData.push(...unmatchedRenderJobs)
        }

        const exportName = this.$title() || `Template (${templateRevisionId})`

        const jobGraph = await createBatchExportJobGraph(this.injector, variationsData, mediaType, templateRevisionId, defaultCustomerId, exportName)

        const jobName = `Batch download generation for template ${templateRevisionId}`

        const onBackendJobCompleted = (jobData: JobData, setResult: (result: {url: string; title: string}) => void) => {
            const resultUrl = jobData.output?.downloadUrl
            const resultTitle = "Download link"

            if (!resultUrl) return
            setResult({url: resultUrl, title: resultTitle})
            FilesService.downloadFile(exportName, resultUrl)
        }

        const runBackendJobOperation = new RunBackendJobOperation(
            this.injector,
            "Download",
            `Preparing: ${exportName}`,
            {jobType: JobType.BatchDownload, name: jobName, graph: jobGraph, organizationLegacyId: defaultCustomerId},
            onBackendJobCompleted,
        )
        this.backgroundOperationService.registerBackgroundOperation(runBackendJobOperation)

        void runBackendJobOperation.submitJob()
    }

    async deleteAll() {
        const variations = this.$allVariations().filter((variation): variation is ConfigurationData & {render: JobData} => variation.render !== undefined)

        const unmatchedRenderJobs = this.$unassignedRenderJobs()

        const dialogRef: MatDialogRef<BatchOperationDialogComponent, BatchOperationDialogResult | undefined> = this.matDialog.open(
            BatchOperationDialogComponent,
            {
                disableClose: false,
                width: DIALOG_DEFAULT_WIDTH,
                data: {
                    configurationCount: variations.length,
                    unassignedCount: unmatchedRenderJobs.length,
                    title: "Discard renders",
                    description: "You are discarding images of",
                    confirmLabel: "Discard",
                    includeUnassignedByDefault: false,
                    destructive: true,
                },
            },
        )

        const result = await firstValueFrom(dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)))
        if (!result) return

        const templateRevisionId = this.localSceneManagerService.$templateRevisionId()
        if (!templateRevisionId) throw Error("No template revision id set")

        const jobsToDelete: {jobId: string; hash?: string}[] = variations.map((variation) => ({
            jobId: variation.render.id,
            hash: variation.hash,
        }))

        if (result.includeUnassigned) {
            jobsToDelete.push(
                ...unmatchedRenderJobs.map(({jobAssignment}) => ({
                    jobId: jobAssignment.job.id,
                })),
            )
        }

        let numDeleted = 0
        let numFailed = 0
        this.$_progress.set(0)
        this.$state.set("loading")

        const cancel$ = this.cancelSubject
        from(jobsToDelete)
            .pipe(
                concatMap((job) => {
                    return from(discardJob(this.injector, job.jobId)).pipe(
                        tap(() => {
                            if (job.hash) {
                                this.$allVariations.update((variations) => variations.map((v) => (v.hash === job.hash ? {...v, render: undefined} : v)))
                            }
                            this.$allRenderJobAssignments.update((assignments) =>
                                assignments.filter((assignment) => assignment.jobAssignment.job.id !== job.jobId),
                            )
                            numDeleted += 1
                            this.$_progress.set(((numDeleted + numFailed) / jobsToDelete.length) * 100)
                        }),
                        catchError((e) => {
                            this.logger.error("Error while discarding job", e)
                            numFailed += 1
                            this.$_progress.set(((numDeleted + numFailed) / jobsToDelete.length) * 100)
                            return of(null)
                        }),
                    )
                }),
                takeUntil(cancel$),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                complete: () => {
                    this.$state.set(numFailed === 0 ? "loaded" : "error")
                    const selectedJobId = this.$selectedUnassignedJob()?.job.id
                    if (selectedJobId && jobsToDelete.some((job) => job.jobId === selectedJobId)) this.$selectedUnassignedJob.set(undefined)
                    this.templateJobManagerService.jobsDiscarded$.next()
                    this.refreshUnassignedJobs()
                    this.notifications.showInfo(
                        `Discarded ${numDeleted} images of configurations.${numFailed > 0 ? ` Failed to discard ${numFailed} images of configurations.` : ""}`,
                    )
                },
            })
    }

    async selectUnassignedJob(job: UnassignedJobData) {
        const refreshedJobQuery = await fetchThrowingErrors(this.jobGql)(job.job)
        const refreshedJob = {...job, job: refreshedJobQuery.job}
        this.$unassignedJobs.update((oldJobs) => oldJobs.map((oldJob) => (oldJob.job.id === job.job.id ? refreshedJob : oldJob)))
        this.$selectedUnassignedJob.set(refreshedJob)
    }

    ngOnDestroy() {
        this.discardPendingVariations()
        this.$selectedUnassignedJob.set(undefined)
        this.templateJobManagerService.$layerMode.set(false)
        this.templateJobManagerService.$layerPlan.set(undefined)
        this.$soloLayer.set(undefined)
        this.$layerJobs.set(new Map())
        this.$sceneManagerService().$layerOutline.set([])

        this.cancelSubject.next()
        this.cancelSubject.complete()
    }
}
